import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { track } from "../lib/mixpanel"

// Timesheets → Foreman's Report (Kloepfer K8-34, 2026-10-09): the office's
// part of the sheet. Bid hours per column and the labor rate save on change
// and the sheet redraws itself (?sheet=1 returns just this block), so the
// yield and the costs follow; the foreman's name and the day's notes save
// on change too. Everyone else sees the sheet read-only.
//
// Move time (Kloepfer K9-19): a person's line opens a pop-up to move part
// of their shift to another job — pick the job, which end of the shift and
// where to cut (a time, or how many hours), with their phone's arrivals and
// leavings as one-click cuts. The server does the move (the day's hours stay
// the same) and the sheet redraws.
const MINUTE = 60 * 1000

export default class extends Controller {
  static targets = ["laborRate", "foreman", "note",
    "moveModal", "moveWho", "moveShift", "moveJob", "moveSide", "moveTime", "moveHours",
    "moveHints", "moveNoHints", "moveSummary", "moveReason", "moveSave"]
  static values = { sheetUrl: String, bidsUrl: String, noteUrl: String, projectId: Number, jobKey: String, day: String, messages: Object,
    moveUrl: String, jobName: String, dayLabel: String }

  async saveBids() {
    const bids = {}
    this.element.querySelectorAll("[data-bid]").forEach((el) => { bids[el.dataset.bid] = el.value.trim() })
    const body = { project_id: this.projectIdValue || null, bids }
    if (this.hasLaborRateTarget) body.labor_rate = this.laborRateTarget.value.trim()
    if (!(await this._patch(this.bidsUrlValue, body))) return
    track("Foreman Report Bids Saved", { project_id: this.projectIdValue })
    await this.refresh()
    showToast(this.messagesValue.saved)
  }

  async saveNote() {
    const body = { job_key: this.jobKeyValue, day: this.dayValue }
    if (this.hasForemanTarget) body.foreman = this.foremanTarget.value.trim()
    if (this.hasNoteTarget) body.note = this.noteTarget.value.trim()
    if (!(await this._patch(this.noteUrlValue, body))) return
    track("Foreman Report Note Saved", { project_id: this.projectIdValue })
    showToast(this.messagesValue.saved)
  }

  // Redraw the whole tab from the server, so the totals, yield and costs follow the saved numbers.
  async refresh() {
    const res = await fetch(this.sheetUrlValue, { headers: { Accept: "text/html" }, credentials: "same-origin" })
    if (!res.ok) return
    const tpl = document.createElement("template")
    tpl.innerHTML = (await res.text()).trim()
    const fresh = tpl.content.querySelector("[data-foreman-tab]")
    const current = this.element.closest("[data-foreman-tab]")
    if (fresh && current) current.replaceWith(fresh)
  }

  // ── Move time (K9-19) ──

  openMove(event) {
    const data = JSON.parse(event.currentTarget.dataset.move || "{}")
    this._move = {
      name: data.name,
      entries: (data.entries || []).map((e) => this._parseEntry(e)),
      visits: (data.visits || []).map((v) => ({ ...v, at: new Date(v.at) }))
    }
    if (!this._move.entries.length) return

    const m = this._moveText()
    this.moveWhoTarget.textContent = this._fill(m.who, { name: data.name, date: this.dayLabelValue, job: this.jobNameValue })
    this.moveShiftTarget.innerHTML = ""
    this._move.entries.forEach((e, i) => {
      const opt = document.createElement("option")
      opt.value = String(i)
      opt.textContent = this._fill(m.shift_label, { from: this._clock(e.start), to: this._clock(e.end), hours: this._hours(e.paid) })
      this.moveShiftTarget.appendChild(opt)
    })
    this.moveJobTarget.value = ""
    this._setSide("after")
    this.moveReasonTarget.value = ""
    this.moveSaveTarget.disabled = false
    this.moveSaveTarget.textContent = m.save
    this._pickShift(0)
    this.moveModalTarget.classList.remove("hidden")
    this._escHandler = (e) => { if (e.key === "Escape") this.closeMove() }
    document.addEventListener("keydown", this._escHandler)
  }

  closeMove() {
    if (!this.hasMoveModalTarget) return
    this.moveModalTarget.classList.add("hidden")
    if (this._escHandler) document.removeEventListener("keydown", this._escHandler)
    this._escHandler = null
  }

  disconnect() {
    if (this._escHandler) document.removeEventListener("keydown", this._escHandler)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  moveShiftChanged() {
    this._pickShift(Number(this.moveShiftTarget.value) || 0)
  }

  moveSideChanged() {
    // Same cut, the other end: the hours follow.
    this._syncHours()
    this.movePreview()
  }

  // A time picked from the list (or typed and Enter).
  moveTimeChanged() {
    const hhmm = this._timeSelect()?.value
    this._cut = hhmm ? this._cutFromHHMM(hhmm) : null
    this._syncHours()
    this.movePreview()
  }

  // A time typed and left without Enter.
  moveTimeTyped(event) {
    const ctrl = this._timeSelect()
    const parsed = ctrl?._parse(event.target.value)
    if (!ctrl || !parsed) return
    ctrl.setValue(parsed)
    this.moveTimeChanged()
  }

  // How many hours move: the cut follows, counting work time only (breaks skipped).
  moveHoursTyped() {
    const e = this._entry
    const hours = parseFloat(this.moveHoursTarget.value.replace(",", "."))
    if (!e || !(hours > 0)) {
      this._cut = null
      this._timeSelect()?.setValue("")
      this.movePreview()
      return
    }
    this._cut = this._cutForSeconds(Math.round(hours * 3600))
    this._timeSelect()?.setValue(this._hhmm(this._cut))
    this.movePreview()
  }

  // One of their phone's arrivals or leavings: cut there; getting to (or
  // leaving) another job also picks that job and which end moves.
  useHint(event) {
    const v = this._hints?.[Number(event.currentTarget.dataset.index)]
    const e = this._entry
    if (!v || !e) return
    const at = new Date(Math.min(Math.max(v.at.getTime(), e.start.getTime()), e.end.getTime()))
    const current = this.projectIdValue || null
    if (v.area === "job" && v.area_id && v.area_id !== current && this._hasJob(v.area_id)) {
      this.moveJobTarget.value = String(v.area_id)
      this._setSide(v.kind === "arrive" ? "after" : "before")
    } else if (v.area === "job" && current && v.area_id === current) {
      this._setSide(v.kind === "leave" ? "after" : "before")
    }
    this._cut = new Date(Math.round(at.getTime() / MINUTE) * MINUTE)
    this._timeSelect()?.setValue(this._hhmm(this._cut))
    this._syncHours()
    this.movePreview()
  }

  movePreview() {
    const m = this._moveText()
    const plan = this._plan()
    const job = this.moveJobTarget.selectedOptions[0]
    const box = this.moveSummaryTarget
    let text
    let ready = false
    if (!plan || !this.moveJobTarget.value) text = m.pick_first
    else if (plan.nothing) text = m.nothing
    else if (plan.breakTooLong) text = m.break_too_long_stay
    else {
      const to = job ? job.textContent.trim() : ""
      text = plan.whole
        ? this._fill(m.summary_all, { moved: this._hours(plan.moved), to })
        : this._fill(m.summary, { moved: this._hours(plan.moved), to, stay: this._hours(plan.stay), from: this.jobNameValue })
      text += " " + this._fill(m.day_same, { total: this._hours(plan.total) })
      if (plan.machinesMove) text += " " + m.machines
      ready = true
    }
    box.textContent = text
    box.classList.toggle("bg-blue-50", ready)
    box.classList.toggle("border-blue-200", ready)
    box.classList.toggle("text-blue-900", ready)
    box.classList.toggle("bg-gray-50", !ready)
    box.classList.toggle("border-gray-200", !ready)
    box.classList.toggle("text-gray-600", !ready)
    this._ready = ready
  }

  async saveMove() {
    const m = this._moveText()
    this.movePreview()
    if (!this._ready) {
      showToast(this.moveSummaryTarget.textContent || m.pick_first, "error")
      return
    }
    const btn = this.moveSaveTarget
    btn.disabled = true
    btn.textContent = m.saving
    try {
      const res = await fetch(this.moveUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          entry_id: this._entry.id, project_id: this.moveJobTarget.value, split_at: this._cut.toISOString(),
          side: this._side(), reason: this.moveReasonTarget.value.trim()
        }),
        credentials: "same-origin"
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || this.messagesValue.failed)
      this.closeMove()
      await this.refresh()
      showToast(data.message || this.messagesValue.saved)
    } catch (err) {
      showToast(err.message || this.messagesValue.failed, "error")
      btn.disabled = false
      btn.textContent = m.save
    }
  }

  // ── Move time helpers ──

  _moveText() {
    return this.messagesValue.move || {}
  }

  _parseEntry(e) {
    const start = new Date(e.start)
    const end = new Date(e.end)
    const breakSeconds = Number(e.break_seconds) || 0
    const breaks = (e.breaks || []).map((b) => ({ start: new Date(b.start), end: new Date(b.end) })).filter((b) => b.end > b.start)
    const sum = breaks.reduce((s, b) => s + (b.end - b.start) / 1000, 0)
    // Breaks with times go with the part they fall in; a break with no times stays on the shift (the server's rule).
    const timed = breaks.length > 0 && Math.abs(sum - breakSeconds) <= 1
    return { id: e.id, start, end, breakSeconds, breaks: timed ? breaks : [], timed,
      paid: Math.max((end - start) / 1000 - breakSeconds, 0), machineHours: Number(e.machine_hours) || 0 }
  }

  _pickShift(index) {
    this._entry = this._move.entries[index] || this._move.entries[0]
    this.moveShiftTarget.value = String(this._move.entries.indexOf(this._entry))
    // One shift: shown for its times, nothing to pick.
    this.moveShiftTarget.disabled = this._move.entries.length < 2
    this._cut = null
    this._timeSelect()?.setValue("")
    this.moveHoursTarget.value = ""
    this._drawHints()
    this.movePreview()
  }

  _drawHints() {
    const e = this._entry
    const m = this._moveText()
    const near = 30 * MINUTE
    this._hints = this._move.visits.filter((v) => v.at >= new Date(e.start - near) && v.at <= new Date(e.end.getTime() + near))
    this.moveHintsTarget.innerHTML = ""
    this._hints.forEach((v, i) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.index = String(i)
      btn.dataset.action = "click->time-foreman#useHint"
      const arrive = v.kind === "arrive"
      btn.className = `inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[13px] cursor-pointer ${arrive ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100" : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"}`
      const time = document.createElement("span")
      time.className = "font-semibold tabular-nums"
      time.textContent = this._clock(v.at)
      const what = document.createElement("span")
      what.textContent = this._fill(arrive ? m.hint_arrive : m.hint_leave, { name: v.name || m.hint_somewhere })
      btn.append(time, what)
      this.moveHintsTarget.appendChild(btn)
    })
    this.moveNoHintsTarget.classList.toggle("hidden", this._hints.length > 0)
  }

  // What the move would do, the server's way: {whole, moved, stay, total} in seconds.
  _plan() {
    const e = this._entry
    const cut = this._cut
    if (!e || !cut) return null
    const after = this._side() === "after"
    const from = Math.max((after ? cut : e.start).getTime(), e.start.getTime())
    const to = Math.min((after ? e.end : cut).getTime(), e.end.getTime())
    if (to <= from) return { nothing: true }
    const span = (e.end - e.start) / 1000
    const stayFrom = after ? e.start.getTime() : to
    const stayTo = after ? from : e.end.getTime()
    // Less than a minute left over: all of it moves.
    const whole = span - (to - from) / 1000 < 60
    if (whole) return { whole, moved: e.paid, stay: 0, total: e.paid }
    const movedBreak = e.timed ? this._overlap(e.breaks, from, to) : 0
    const moved = (to - from) / 1000 - movedBreak
    if (moved <= 0) return { nothing: true }
    if (!e.timed && e.breakSeconds > 0 && e.breakSeconds >= (stayTo - stayFrom) / 1000) return { breakTooLong: true }
    const stay = e.paid - moved
    const cap = Math.ceil((stay / 3600) * 4) / 4
    return { whole, moved, stay, total: e.paid, machinesMove: e.machineHours > cap + 1e-9 }
  }

  _overlap(breaks, from, to) {
    return breaks.reduce((s, b) => s + Math.max(Math.min(b.end.getTime(), to) - Math.max(b.start.getTime(), from), 0) / 1000, 0)
  }

  // Where to cut so `seconds` of work (breaks with times skipped) move.
  _cutForSeconds(seconds) {
    const e = this._entry
    const after = this._side() === "after"
    if (seconds >= e.paid - 1) return after ? new Date(e.start) : new Date(e.end)
    // The shift's work stretches: the shift minus its timed breaks.
    const segments = []
    let at = e.start.getTime()
    e.breaks.slice().sort((a, b) => a.start - b.start).forEach((b) => {
      if (b.start.getTime() > at) segments.push([at, b.start.getTime()])
      at = Math.max(at, b.end.getTime())
    })
    if (e.end.getTime() > at) segments.push([at, e.end.getTime()])
    let left = seconds * 1000
    const list = after ? segments.slice().reverse() : segments
    for (const [s, t] of list) {
      const len = t - s
      if (left <= len) {
        const cut = after ? t - left : s + left
        return new Date(Math.round(cut / MINUTE) * MINUTE)
      }
      left -= len
    }
    return after ? new Date(e.start) : new Date(e.end)
  }

  _syncHours() {
    const plan = this._plan()
    this.moveHoursTarget.value = plan && !plan.nothing && !plan.breakTooLong ? this._hours(plan.moved) : ""
  }

  // "HH:MM" on the shift's own day (the next day for a shift past midnight).
  _cutFromHHMM(hhmm) {
    const e = this._entry
    const [h, mm] = hhmm.split(":").map((x) => parseInt(x, 10))
    let d = new Date(e.start)
    d.setHours(h, mm, 0, 0)
    if (d.getTime() < e.start.getTime() - MINUTE && d.getTime() + 24 * 60 * MINUTE <= e.end.getTime() + MINUTE) {
      d = new Date(d.getTime() + 24 * 60 * MINUTE)
    }
    return d
  }

  _hhmm(date) {
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
  }

  _clock(date) {
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
  }

  _hours(seconds) {
    return String(Math.round((seconds / 3600) * 100) / 100)
  }

  _side() {
    return this.moveSideTargets.find((r) => r.checked)?.value || "after"
  }

  _setSide(value) {
    this.moveSideTargets.forEach((r) => { r.checked = r.value === value })
  }

  _hasJob(id) {
    return Array.from(this.moveJobTarget.options).some((o) => o.value === String(id))
  }

  _timeSelect() {
    return this.application.getControllerForElementAndIdentifier(this.moveTimeTarget, "time-select")
  }

  _fill(template, vars) {
    return String(template || "").replace(/%\{(\w+)\}/g, (_, k) => (vars[k] ?? ""))
  }

  async _patch(url, body) {
    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return true
    } catch (_) {
      showToast(this.messagesValue.failed, "error")
      return false
    }
  }
}
